import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { TaskCategoryType } from "@/lib/taskCategoryConfig";

interface CustomField {
  field_key: string;
  field_label: string;
  field_type: 'text' | 'number' | 'date' | 'select' | 'multiselect' | 'currency' | 'percentage' | 'url' | 'email' | 'phone' | 'textarea';
  field_options?: string[];
  is_required: boolean;
  field_value?: any;
}

interface CustomFieldsEditorProps {
  category: TaskCategoryType;
  values: Record<string, any>;
  onChange: (values: Record<string, any>) => void;
}

// Define custom fields for each category
const CATEGORY_CUSTOM_FIELDS: Record<string, CustomField[]> = {
  financial: [
    { field_key: 'budget_amount', field_label: 'Budget Amount', field_type: 'currency', is_required: false },
    { field_key: 'account_number', field_label: 'Account Number', field_type: 'text', is_required: false },
    { field_key: 'fiscal_year', field_label: 'Fiscal Year', field_type: 'text', is_required: false },
  ],
  legal: [
    { field_key: 'jurisdiction', field_label: 'Jurisdiction', field_type: 'text', is_required: false },
    { field_key: 'filing_number', field_label: 'Filing Number', field_type: 'text', is_required: false },
    { field_key: 'compliance_deadline', field_label: 'Compliance Deadline', field_type: 'date', is_required: false },
  ],
  governance: [
    { field_key: 'meeting_type', field_label: 'Meeting Type', field_type: 'select', field_options: ['Regular Meeting and Observance', 'Board Meeting', 'Recurring Meeting', 'Special Meeting'], is_required: false },
    { field_key: 'quorum_required', field_label: 'Quorum Required', field_type: 'number', is_required: false },
    { field_key: 'vote_result', field_label: 'Vote Result', field_type: 'select', field_options: ['Passed', 'Failed', 'Tabled'], is_required: false },
  ],
  membership: [
    { field_key: 'member_count', field_label: 'Number of Members', field_type: 'number', is_required: false },
    { field_key: 'membership_tier', field_label: 'Membership Tier', field_type: 'select', field_options: ['Active', 'Associate', 'Honorary'], is_required: false },
  ],
  asset_management: [
    { field_key: 'asset_value', field_label: 'Asset Value', field_type: 'currency', is_required: false },
    { field_key: 'acquisition_date', field_label: 'Acquisition Date', field_type: 'date', is_required: false },
    { field_key: 'asset_location', field_label: 'Asset Location', field_type: 'text', is_required: false },
  ],
  operations: [
    { field_key: 'sop_reference', field_label: 'SOP Reference', field_type: 'text', is_required: false },
    { field_key: 'completion_percentage', field_label: 'Completion %', field_type: 'percentage', is_required: false },
  ],
  communication: [
    { field_key: 'target_audience', field_label: 'Target Audience', field_type: 'text', is_required: false },
    { field_key: 'communication_channel', field_label: 'Channel', field_type: 'select', field_options: ['Email', 'Newsletter', 'Website', 'Social Media'], is_required: false },
  ],
};

export function CustomFieldsEditor({ category, values, onChange }: CustomFieldsEditorProps) {
  const fields = CATEGORY_CUSTOM_FIELDS[category] || [];

  if (fields.length === 0) return null;

  const handleFieldChange = (fieldKey: string, value: any) => {
    onChange({
      ...values,
      [fieldKey]: value,
    });
  };

  const renderField = (field: CustomField) => {
    const value = values[field.field_key];

    switch (field.field_type) {
      case 'text':
      case 'email':
      case 'phone':
      case 'url':
        return (
          <Input
            type={field.field_type}
            value={value || ''}
            onChange={(e) => handleFieldChange(field.field_key, e.target.value)}
            placeholder={field.field_label}
          />
        );

      case 'textarea':
        return (
          <Textarea
            value={value || ''}
            onChange={(e) => handleFieldChange(field.field_key, e.target.value)}
            placeholder={field.field_label}
            rows={3}
          />
        );

      case 'number':
      case 'percentage':
        return (
          <Input
            type="number"
            value={value || ''}
            onChange={(e) => handleFieldChange(field.field_key, parseFloat(e.target.value) || 0)}
            placeholder={field.field_label}
            step={field.field_type === 'percentage' ? '1' : 'any'}
            max={field.field_type === 'percentage' ? 100 : undefined}
            min={field.field_type === 'percentage' ? 0 : undefined}
          />
        );

      case 'currency':
        return (
          <Input
            type="number"
            value={value || ''}
            onChange={(e) => handleFieldChange(field.field_key, parseFloat(e.target.value) || 0)}
            placeholder="0.00"
            step="0.01"
            min="0"
          />
        );

      case 'date':
        return (
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal",
                  !value && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {value ? format(new Date(value), "PPP") : "Pick a date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={value ? new Date(value) : undefined}
                onSelect={(date) => handleFieldChange(field.field_key, date?.toISOString().split('T')[0])}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        );

      case 'select':
        return (
          <Select
            value={value || ''}
            onValueChange={(val) => handleFieldChange(field.field_key, val)}
          >
            <SelectTrigger>
              <SelectValue placeholder={`Select ${field.field_label}`} />
            </SelectTrigger>
            <SelectContent>
              {(field.field_options || []).map((option) => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-4 p-4 border rounded-lg">
      <h3 className="font-medium">Category-Specific Fields</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {fields.map((field) => (
          <div key={field.field_key} className="space-y-2">
            <Label>
              {field.field_label}
              {field.is_required && <span className="text-destructive ml-1">*</span>}
            </Label>
            {renderField(field)}
          </div>
        ))}
      </div>
    </div>
  );
}
